import { actions as notificationActions } from "reducers/notification";

import * as i18n from "util/i18n";
import isTokenActive from "util/token";
import Yup from "yup";

const LABEL = "frequentDestination.create.confirmate";
const OTP_REQUIRED_MSG = () => i18n.get("accounts.new.account.otp.error.required");

/**
 * Soft-Token UX for Agenda (create/modify/delete).
 * Misma condición que transferencias / Credin / currency exchange:
 * `!isDesktop && isTokenActive()`.
 */
export function buildAgendaSoftTokenConfirmProps(tokenNumber, isDesktop) {
    const softTokenActive = !isDesktop && isTokenActive();
    if (!softTokenActive) {
        return {
            softTokenActive: false,
            confirmKey: "manual",
            getToken: undefined,
            confirmationText: undefined,
            validationConfirm: Yup.object().shape({
                otp: Yup.string().required(OTP_REQUIRED_MSG()),
            }),
            resolveOtp: (values) => values?.otp || "",
        };
    }
    return {
        softTokenActive: true,
        confirmKey: `soft-${tokenNumber || "pending"}`,
        // Si aún no hay token, Confirmation muestra OTP manual (fallback).
        getToken: () => Promise.resolve({ token: tokenNumber || null }),
        confirmationText: i18n.get("confirmation.withoutotp.text"),
        validationConfirm: Yup.object().shape({
            otp: tokenNumber ? Yup.string().nullable() : Yup.string().required(OTP_REQUIRED_MSG()),
        }),
        resolveOtp: (values) => tokenNumber || values?.otp || "",
    };
}

function bankLabel(destination) {
    if (!destination?.cbu) {
        return "";
    }
    if (destination.cbu.startsWith("000")) {
        return destination.localBank || "";
    }
    return `${destination.localBank || ""}/${destination.subsidiaryBank || ""}`;
}

function ownLabel(destination) {
    return destination?.isOwn === "1" ? "SI" : "NO";
}

/**
 * Read-only account lines for the V3 modify form. Reference and email stay editable.
 */
export function buildAccountSummaryLines(destination) {
    if (!destination) {
        return [];
    }
    const bankKey = destination.cbu?.startsWith("000") ? "entity" : "bank";
    return [
        { label: i18n.get(`${LABEL}.numberAccount`), value: destination.accountNumber || "" },
        { label: i18n.get(`${LABEL}.accountType`), value: destination.productType || "" },
        { label: i18n.get(`${LABEL}.own`), value: ownLabel(destination) },
        { label: i18n.get(`${LABEL}.cbu`), value: destination.cbu || "" },
        {
            label: i18n.get(`${LABEL}.document`),
            value: `${destination.recipientDocumentType || ""} ${destination.recipientDocumentNumber || ""}`.trim(),
        },
        { label: i18n.get(`${LABEL}.recipientName`), value: destination.recipientName || "" },
        { label: i18n.get(`${LABEL}.${bankKey}`), value: bankLabel(destination) },
    ];
}

/**
 * Receipt rows for create, modify and delete confirmation.
 * `reference` and `email` override the stored contact when the preview already changed them.
 */
export function buildFrequentDestinationConfirmFields(destination, { alias, reference, email } = {}) {
    if (!destination) {
        return [];
    }
    const fields = buildAccountSummaryLines(destination).map((line) => ({
        title: line.label,
        value: line.value,
    }));
    if (alias) {
        fields.splice(4, 0, { title: i18n.get(`${LABEL}.alias`), value: alias });
    }
    fields.push(
        {
            title: i18n.get(`${LABEL}.email`),
            value: (email ?? destination.recipientEmail) || "-",
        },
        { title: i18n.get(`${LABEL}.referent`), value: reference ?? destination.name ?? "" },
    );
    return fields;
}

/**
 * Minimal Formik bag so the existing OTP sagas can report field errors as a snackbar.
 */
export function otpFormikBag(dispatch, scopes) {
    return {
        setSubmitting: () => {},
        setErrors: (errors) => {
            const message =
                (errors && (errors.otp || errors.NO_FIELD || errors._otp)) || i18n.get("global.unexpectedError");
            dispatch(notificationActions.showNotification(String(message), "error", scopes));
        },
    };
}
